import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSiteAssets } from "@/lib/siteAssets";
import { imageUrl } from "@/components/ImageUpload";
import { Lock, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const [verifyState, setVerifyState] = useState("checking"); // checking | valid | invalid
    const [tokenEmail, setTokenEmail] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const assets = useSiteAssets();

    useEffect(() => {
        if (!token) { setVerifyState("invalid"); return; }
        axios.get(`${BACKEND_URL}/api/auth/reset-password/verify`, { params: { token } })
            .then((r) => { setVerifyState("valid"); setTokenEmail(r.data.email || ""); })
            .catch(() => setVerifyState("invalid"));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.length < 8) return setError("Password must be at least 8 characters.");
        if (pw !== pw2) return setError("Passwords do not match.");
        setBusy(true);
        try {
            await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: pw });
            setDone(true);
            setTimeout(() => nav("/login"), 2200);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not reset password. The link may have expired.");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:block relative bg-brand-bone">
                <img src={imageUrl(assets.login_background)} className="absolute inset-0 w-full h-full object-cover" alt="" />
            </div>
            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-sm" data-testid="reset-password-page">
                    <Link to="/" className="text-sm text-brand-mute hover:text-brand-forest">← Back to HealthFairX</Link>
                    <h1 className="font-heading text-3xl font-extrabold mt-6">Choose a new password</h1>

                    {verifyState === "checking" && <p className="mt-6 text-sm text-brand-mute">Verifying your reset link…</p>}

                    {verifyState === "invalid" && (
                        <div className="mt-6 rounded-xl border border-brand-coral/30 bg-brand-coral/5 p-6" data-testid="reset-invalid">
                            <h2 className="font-heading font-bold text-lg">This link isn't valid</h2>
                            <p className="text-sm text-brand-mute mt-2">It may have already been used or expired. Reset links are valid for 1 hour.</p>
                            <Link to="/forgot-password" className="mt-5 inline-block pill btn-coral px-5 py-2.5 text-sm font-semibold">Request a new link</Link>
                        </div>
                    )}

                    {verifyState === "valid" && !done && (
                        <>
                            {tokenEmail && <p className="mt-2 text-sm text-brand-mute">Setting a new password for <strong>{tokenEmail}</strong>.</p>}
                            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form" noValidate>
                                <div>
                                    <label className="label-base">New password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" size={16} />
                                        <input data-testid="reset-pw-input" type="password" required minLength={8} className="input-base pl-10" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-base">Confirm new password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mute" size={16} />
                                        <input data-testid="reset-pw2-input" type="password" required minLength={8} className="input-base pl-10" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-type the password" />
                                    </div>
                                </div>
                                {error && <div className="text-sm text-brand-coral" data-testid="reset-error">{error}</div>}
                                <button data-testid="reset-submit" disabled={busy} className="w-full pill btn-coral px-5 py-3 text-sm font-semibold disabled:opacity-60">{busy ? "Updating…" : "Update password"}</button>
                            </form>
                        </>
                    )}

                    {done && (
                        <div className="mt-6 rounded-xl border border-brand-line bg-white p-6" data-testid="reset-success">
                            <CheckCircle2 className="text-brand-forest" size={28} />
                            <h2 className="font-heading font-bold text-lg mt-3">Password updated</h2>
                            <p className="text-sm text-brand-mute mt-2">Redirecting you to login…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
